import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, EmptyState, Loader, Button } from "@/components/PageUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Plus, UserRound, Trash2, UserCog, Image } from "lucide-react";
import { TeamPhotos } from "@/components/TeamPhotos";

export default function Teams() {
  const [teams, setTeams] = useState(null);
  const [open, setOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(null);
  const [photosOpen, setPhotosOpen] = useState(null);
  const [form, setForm] = useState({ name: "", age_group: "", season: "" });
  const [staff, setStaff] = useState({ name: "", role: "coach", email: "" });

  const load = () => api.get("/teams").then((r) => setTeams(r.data)).catch(() => setTeams([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) return toast.error("Team name required");
    try {
      await api.post("/teams", form);
      toast.success("Team created");
      setOpen(false); setForm({ name: "", age_group: "", season: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this team and its athletes?")) return;
    await api.delete(`/teams/${id}`); load();
  };

  const [addingStaff, setAddingStaff] = useState(false);
  const addStaff = async () => {
    if (!staff.name) return toast.error("Name required");
    setAddingStaff(true);
    try {
      await api.post(`/teams/${staffOpen}/staff`, staff);
      toast.success("Staff added"); setStaff({ name: "", role: "coach", email: "" }); setStaffOpen(null); load();
    } catch (e) { toast.error(apiErr(e, "Could not add staff")); } finally { setAddingStaff(false); }
  };

  if (!teams) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Teams" subtitle="Unlimited teams — U8 to Varsity, boys, girls, adult." testid="teams-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="brand-gradient text-white border-0 hover:opacity-90" data-testid="add-team-btn"><Plus size={16} className="mr-1.5" /> New team</Button>
            </DialogTrigger>
            <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg rounded-2xl" data-testid="team-dialog">
              <DialogHeader><DialogTitle>Create team</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Team name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="U12 Boys" className="mt-1.5 h-11" data-testid="team-name-input" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Age group</Label><Input value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} placeholder="U12" className="mt-1.5 h-11" /></div>
                  <div><Label>Season</Label><Input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="2026 Spring" className="mt-1.5 h-11" /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="brand-gradient text-white border-0" data-testid="save-team-btn">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      {teams.length === 0 ? (
        <EmptyState icon={Users} title="No teams yet" desc="Create your first team to start adding athletes." testid="empty-teams" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((t) => (
            <div key={t.id} className="rounded-2xl border border-border bg-card p-6 hover:-translate-y-1 transition-transform duration-200" data-testid={`team-card-${t.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-bold text-lg">{t.name}</h3>
                  <p className="text-sm text-muted-foreground">{t.age_group} {t.season && `· ${t.season}`}</p>
                </div>
                <button onClick={() => del(t.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-team-${t.id}`}><Trash2 size={16} /></button>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
                <UserRound size={16} /> {t.player_count} athletes
              </div>
              {t.staff?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {t.staff.map((s) => <span key={s.id} className="text-xs bg-accent px-2 py-1 rounded-md capitalize">{s.name} · {s.role}</span>)}
                </div>
              )}
              <Button size="sm" variant="outline" className="mt-4 w-full" onClick={() => setStaffOpen(t.id)} data-testid={`add-staff-${t.id}`}>
                <UserCog size={15} className="mr-1.5" /> Add staff
              </Button>
              <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => setPhotosOpen(t)} data-testid={`team-photos-btn-${t.id}`}>
                <Image size={15} className="mr-1.5" /> Team photos
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!staffOpen} onOpenChange={(v) => !v && setStaffOpen(null)}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>Add staff member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Name</Label><Input value={staff.name} onChange={(e) => setStaff({ ...staff, name: e.target.value })} className="mt-1.5" data-testid="staff-name-input" /></div>
            <div><Label>Role</Label>
              <Select value={staff.role} onValueChange={(v) => setStaff({ ...staff, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="assistant">Assistant Coach</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Email</Label><Input value={staff.email} onChange={(e) => setStaff({ ...staff, email: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={addStaff} disabled={addingStaff} className="brand-gradient text-white border-0" data-testid="save-staff-btn">{addingStaff ? "Adding..." : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!photosOpen} onOpenChange={(v) => !v && setPhotosOpen(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="team-photos-dialog">
          <DialogHeader><DialogTitle>{photosOpen?.name} · Team photos</DialogTitle></DialogHeader>
          {photosOpen && <TeamPhotos teamId={photosOpen.id} canUpload showUpsell />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
